import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

type Language = "pt" | "de";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// German translations for employee interface
const translations: Record<string, Record<string, string>> = {
  de: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.models": "Models",
    "nav.myModels": "Meine Models",
    "nav.chatters": "Chatters",
    "nav.chatterFines": "Chatter-Strafen",
    "nav.employees": "Mitarbeiter",
    "nav.users": "Benutzer",
    "nav.payments": "Zahlungen",
    "nav.postManager": "Post-Manager",
    "nav.calendar": "Kalender",
    "nav.exclusiveContent": "Exklusiver Inhalt",
    "nav.chat": "Chat",
    "nav.internalChat": "Interner Chat",
    "nav.financial": "Finanzen",
    "nav.settings": "Einstellungen",
    "nav.adminPanel": "Admin-Panel",
    "nav.logout": "Abmelden",
    
    // Employee types
    "employeeType.chatter": "Chatter",
    "employeeType.content_manager": "Content Admin",
    "employeeType.co_admin": "Co-Admin",
    "employeeType.supervisor_chatter": "Supervisor Chatter",
    "employeeType.admin": "Administrator",
    "employeeType.recrutador": "Recruiter",
    
    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.welcome": "Willkommen zurück",
    "dashboard.todayEarnings": "Heutige Einnahmen",
    "dashboard.monthEarnings": "Monatliche Einnahmen",
    "dashboard.totalEarnings": "Gesamteinnahmen",
    "dashboard.activeModels": "Aktive Models",
    "dashboard.pendingContent": "Ausstehender Inhalt",
    "dashboard.pipelineProgress": "Pipeline-Fortschritt",
    "dashboard.recentActivity": "Letzte Aktivität",
    "dashboard.quickActions": "Schnellaktionen",
    "dashboard.viewAll": "Alle anzeigen",
    "dashboard.noModels": "Keine Models gefunden",
    "dashboard.connectedModels": "Verbundene Models",
    "dashboard.loading": "Laden...",
    "dashboard.summary": "Hier ist die Zusammenfassung Ihres Tages",
    "dashboard.quickAccess": "Schnellzugriff",
    "dashboard.statistics": "Statistiken",
    "dashboard.noActivity": "Keine aktuelle Aktivität anzuzeigen.",
    "dashboard.totalChatters": "Chatters Gesamt",
    "dashboard.connectedModelsCount": "Verbundene Models",
    "dashboard.totalModelsCount": "Models Gesamt",
    "dashboard.pendingCalendars": "Ausstehende Kalender",
    "dashboard.overdue": "überfällig",
    "dashboard.pendingContentCount": "Ausstehender Inhalt",
    "dashboard.completedToday": "Heute abgeschlossen",
    "dashboard.totalEarningsAmount": "Gesamteinnahmen",
    "dashboard.employee": "Mitarbeiter",
    "dashboard.goodMorning": "Guten Morgen",
    "dashboard.goodAfternoon": "Guten Tag",
    "dashboard.goodEvening": "Guten Abend",
    "dashboard.chatterCode": "Code für neue Chatters",
    "dashboard.shareCode": "Teilen Sie diesen Code für Chatters, um sich zu registrieren",
    
    // Quick Actions
    "quickAction.openChat": "Chat öffnen",
    "quickAction.talkToModels": "Mit Models sprechen",
    "quickAction.calendar": "Kalender",
    "quickAction.pending": "ausstehend",
    "quickAction.postManager": "Post-Manager",
    "quickAction.managePosts": "Beiträge verwalten",
    "quickAction.exclusiveContent": "Exklusiver Inhalt",
    "quickAction.delivered": "geliefert",
    "quickAction.financial": "Finanzen",
    "quickAction.financialChat": "Finanz-Chat",
    "quickAction.registerSales": "Verkäufe registrieren",
    "quickAction.viewModels": "Models anzeigen",
    "quickAction.myModels": "Meine Models",
    "quickAction.registered": "registriert",
    
    // Models
    "models.title": "Models",
    "models.search": "Models suchen...",
    "models.searchByName": "Nach Künstlernamen suchen...",
    "models.searchByAll": "Nach Name, Benutzername oder E-Mail suchen...",
    "models.filter": "Filtern",
    "models.all": "Alle",
    "models.active": "Aktiv",
    "models.inactive": "Inaktiv",
    "models.noResults": "Keine Models gefunden",
    "models.earnings": "Einnahmen",
    "models.day": "Tag",
    "models.month": "Monat",
    "models.total": "Gesamt",
    "models.level": "Level",
    "models.viewDetails": "Details anzeigen",
    "models.pipeline": "Pipeline",
    "models.status": "Status",
    "models.manageAll": "Alle registrierten Models verwalten",
    "models.activeCount": "aktiv",
    "models.inProgressCount": "in Bearbeitung",
    "models.inProgress": "IN BEARBEITUNG",
    "models.activeModels": "AKTIVE MODELS",
    "models.noActiveModels": "Noch keine aktiven Models.",
    "models.noInProgressModels": "Keine Models in Bearbeitung.",
    "models.noPipeline": "Keine Pipeline",
    "models.myModelsTitle": "MEINE MODELS",
    "models.connectedToYou": "mit Ihnen verbunden",
    "models.connectedModels": "VERBUNDENE MODELS",
    "models.waitingConnection": "Kein Model verbunden. Warten Sie darauf, dass der Admin Models mit Ihnen verbindet.",
    "models.noModelFound": "Kein Model gefunden.",
    "models.adminInfo": "Admin-Informationen",
    "models.noAdditionalInfo": "Keine zusätzlichen Informationen verfügbar.",
    "models.simulation": "verfügbar (Simulation)",
    "models.viewingAs": "Sie sehen als",
    "models.selectChatter": "Wählen Sie einen Chatter, um dessen verbundene Models zu sehen.",
    "models.chatterNoModels": "Dieser Chatter hat keine verbundenen Models.",
    "models.selectChatterAbove": "Wählen Sie oben einen Chatter aus, um die verbundenen Models zu sehen.",
    
    // Model Detail
    "modelDetail.profile": "Profil",
    "modelDetail.earnings": "Einnahmen",
    "modelDetail.calendar": "Kalender",
    "modelDetail.content": "Inhalt",
    "modelDetail.pipeline": "Pipeline",
    "modelDetail.notes": "Notizen",
    "modelDetail.questions": "Fragen",
    "modelDetail.documents": "Dokumente",
    "modelDetail.contact": "Kontakt",
    "modelDetail.artisticName": "Künstlername",
    "modelDetail.fullName": "Vollständiger Name",
    "modelDetail.email": "E-Mail",
    "modelDetail.phone": "Telefon",
    "modelDetail.instagram": "Instagram",
    "modelDetail.address": "Adresse",
    "modelDetail.createdAt": "Erstellt am",
    
    // Calendar
    "calendar.title": "Kalender",
    "calendar.deadline": "Frist",
    "calendar.setsRequired": "Erforderliche Sets",
    "calendar.photosRequired": "Erforderliche Fotos/Videos",
    "calendar.delivered": "Geliefert",
    "calendar.pending": "Ausstehend",
    "calendar.completed": "Abgeschlossen",
    "calendar.overdue": "Überfällig",
    "calendar.fine": "Strafe",
    "calendar.approve": "Genehmigen",
    "calendar.reject": "Ablehnen",
    "calendar.addEntry": "Eintrag hinzufügen",
    
    // Exclusive Content
    "exclusiveContent.title": "Exklusiver Inhalt",
    "exclusiveContent.newRequest": "Neue Anfrage",
    "exclusiveContent.pending": "Ausstehend",
    "exclusiveContent.delivered": "Geliefert",
    "exclusiveContent.all": "Alle",
    "exclusiveContent.requestText": "Anfragetext",
    "exclusiveContent.driveLink": "Drive-Link",
    "exclusiveContent.markDelivered": "Als geliefert markieren",
    "exclusiveContent.complaint": "Beschwerde",
    "exclusiveContent.reapprove": "Erneut genehmigen",
    "exclusiveContent.noRequests": "Keine Anfragen gefunden",
    "exclusiveContent.createRequest": "Anfrage erstellen",
    "exclusiveContent.selectModel": "Model auswählen",
    "exclusiveContent.description": "Beschreibung",
    "exclusiveContent.example": "Beispielbild (optional)",
    
    // Chat
    "chat.title": "Chat",
    "chat.typeMessage": "Nachricht eingeben...",
    "chat.send": "Senden",
    "chat.noMessages": "Keine Nachrichten",
    "chat.selectConversation": "Wählen Sie eine Konversation aus, um zu beginnen",
    "chat.searchContacts": "Suchen...",
    "chat.online": "Online",
    "chat.offline": "Offline",
    "chat.unread": "Ungelesen",
    "chat.employees": "Mitarbeiter",
    "chat.models": "Models",
    "chat.fixedContacts": "Feste Kontakte",
    "chat.noContactsAvailable": "Keine Kontakte verfügbar",
    "chat.waitingConfig": "Warten Sie auf die Teamkonfiguration",
    "chat.startConversation": "Starten Sie die Konversation, indem Sie eine Nachricht senden",
    "chat.noUserFound": "Kein Benutzer gefunden",
    "chat.you": "Sie",
    "chat.image": "Bild",
    "chat.viewingAsChatter": "Anzeige als Chatter:",
    "chat.select": "Auswählen...",
    
    // Financial
    "financial.title": "Finanzen",
    "financial.thisMonth": "Diesen Monat",
    "financial.totalGeneral": "Gesamtübersicht",
    "financial.history": "Verlauf",
    "financial.grossEarnings": "Bruttoeinnahmen",
    "financial.netEarnings": "Nettoeinnahmen",
    "financial.agencyShare": "Agenturanteil",
    "financial.modelShare": "Model-Anteil",
    "financial.chatterShare": "Chatter-Anteil",
    "financial.platformFees": "Plattformgebühren",
    "financial.fines": "Strafen",
    "financial.chatSales": "Chat-Verkäufe",
    "financial.feedSales": "Feed-Verkäufe",
    "financial.revenueSplit": "Einnahmenverteilung",
    "financial.registerEarning": "Einnahme registrieren",
    "financial.amount": "Betrag",
    "financial.description": "Beschreibung",
    "financial.source": "Quelle",
    "financial.date": "Datum",
    "financial.myFines": "Meine Strafen",
    "financial.balance": "Guthaben",
    
    // Settings
    "settings.title": "EINSTELLUNGEN",
    "settings.subtitle": "Verwalten Sie Ihre persönlichen Daten und Ihr Passwort",
    "settings.profile": "Profil",
    "settings.notifications": "Benachrichtigungen",
    "settings.language": "Sprache",
    "settings.languageDescription": "Wählen Sie Ihre bevorzugte Sprache für die Benutzeroberfläche",
    "settings.portuguese": "Portugiesisch",
    "settings.german": "Deutsch",
    "settings.save": "Speichern",
    "settings.saved": "Gespeichert",
    "settings.saving": "Speichern...",
    "settings.profilePhoto": "Profilfoto",
    "settings.changePhoto": "Foto ändern",
    "settings.photoFormats": "JPG, PNG oder GIF. Maximal 5MB.",
    "settings.personalInfo": "Persönliche Informationen",
    "settings.username": "Benutzername",
    "settings.usernameNoChange": "Der Benutzername kann nicht geändert werden",
    "settings.registrationEmail": "Registrierungs-E-Mail",
    "settings.emailNoChange": "Die Registrierungs-E-Mail kann nicht geändert werden",
    "settings.phoneNumber": "Handynummer",
    "settings.saveInfo": "Informationen speichern",
    "settings.changePassword": "Passwort ändern",
    "settings.currentPassword": "Aktuelles Passwort",
    "settings.newPassword": "Neues Passwort",
    "settings.confirmNewPassword": "Neues Passwort bestätigen",
    "settings.passwordMinLength": "Das neue Passwort muss mindestens 6 Zeichen haben",
    "settings.viewMode": "Ansichtsmodus",
    "settings.viewingAs": "Sie sehen die Oberfläche als",
    "settings.employeeCan": "Auf dieser Seite kann der Mitarbeiter:",
    "settings.canChangePhoto": "Sein Profilfoto ändern",
    "settings.canUpdatePhone": "Seine Handynummer aktualisieren",
    "settings.canChangePassword": "Sein Zugangspasswort ändern",
    "settings.securityNote": "Aus Sicherheitsgründen werden die persönlichen Daten und Anmeldedaten des Mitarbeiters hier nicht angezeigt.",
    "settings.languageLockedTitle": "Sprache gesperrt",
    "settings.languageLockedDesc": "Für Ihren Mitarbeitertyp ist die Standardsprache auf Deutsch eingestellt und kann nicht geändert werden.",
    
    // Chatters (Supervisor)
    "chatters.title": "Chatters",
    "chatters.addChatter": "Chatter hinzufügen",
    "chatters.connectedModels": "Verbundene Models",
    "chatters.fines": "Strafen",
    "chatters.earnings": "Einnahmen",
    "chatters.addFine": "Strafe hinzufügen",
    "chatters.fineAmount": "Strafbetrag",
    "chatters.fineReason": "Grund",
    "chatters.connectModel": "Model verbinden",
    "chatters.disconnectModel": "Model trennen",
    
    // Pipeline stages
    "pipeline.contract": "Vertrag",
    "pipeline.accountCreation": "Kontoerstellung",
    "pipeline.verification": "Verifizierung",
    "pipeline.contentStart": "Inhaltsstart",
    "pipeline.postingStart": "Posting-Start",
    "pipeline.firstSale": "Erster Verkauf",
    "pipeline.completed": "Abgeschlossen",
    "pipeline.inProgress": "In Bearbeitung",
    "pipeline.pending": "Ausstehend",
    
    // Common
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.delete": "Löschen",
    "common.edit": "Bearbeiten",
    "common.add": "Hinzufügen",
    "common.search": "Suchen",
    "common.filter": "Filtern",
    "common.loading": "Laden...",
    "common.error": "Fehler",
    "common.success": "Erfolg",
    "common.confirm": "Bestätigen",
    "common.back": "Zurück",
    "common.next": "Weiter",
    "common.previous": "Zurück",
    "common.yes": "Ja",
    "common.no": "Nein",
    "common.close": "Schließen",
    "common.open": "Öffnen",
    "common.view": "Anzeigen",
    "common.download": "Herunterladen",
    "common.upload": "Hochladen",
    "common.noData": "Keine Daten verfügbar",
    "common.actions": "Aktionen",
    "common.status": "Status",
    "common.date": "Datum",
    "common.time": "Zeit",
    "common.today": "Heute",
    "common.yesterday": "Gestern",
    "common.thisWeek": "Diese Woche",
    "common.thisMonth": "Diesen Monat",
    "common.total": "Gesamt",
    "common.model": "Model",
    "common.value": "Wert",
    "common.copied": "Kopiert!",
    "common.copyError": "Fehler beim Kopieren",
    
    // Viewing as banner
    "viewingAs.label": "Ansicht als:",
    "viewingAs.backToAdmin": "Zurück zum Admin",
    
    // Payments
    "payments.title": "ZAHLUNGSANFRAGEN",
    "payments.subtitle": "Auszahlungsanfragen der Models verwalten",
    "payments.pending": "Ausstehend",
    "payments.approved": "Genehmigt",
    "payments.rejected": "Abgelehnt",
    "payments.paid": "Bezahlt",
    "payments.all": "Alle",
    "payments.amount": "Betrag",
    "payments.requestDate": "Anfragedatum",
    "payments.reviewDate": "Überprüfungsdatum",
    "payments.approve": "Genehmigen",
    "payments.reject": "Ablehnen",
    "payments.pixKey": "PIX-Schlüssel",
    "payments.wiseId": "Wise ID",
    "payments.searchModel": "Nach Model suchen...",
    "payments.noRequests": "Keine Anfragen gefunden",
    "payments.requestDetails": "Anfragedetails",
    "payments.requestedAmount": "Angeforderter Betrag",
    "payments.paymentMethod": "Zahlungsmethode",
    "payments.requestedAt": "Angefragt am",
    "payments.status": "Status",
    "payments.notes": "Anmerkungen",
    "payments.noNotes": "Keine Anmerkungen",
    "payments.confirmApproval": "Genehmigung bestätigen",
    "payments.confirmApprovalDesc": "Sind Sie sicher, dass Sie diese Auszahlung von {amount} genehmigen möchten?",
    "payments.rejectRequest": "Anfrage ablehnen",
    "payments.rejectReason": "Begründung für die Ablehnung",
    "payments.rejectReasonPlaceholder": "Erklären Sie, warum die Anfrage abgelehnt wird...",
    "payments.approvalSuccess": "Anfrage erfolgreich genehmigt!",
    "payments.rejectionSuccess": "Anfrage abgelehnt",
    "payments.paidSuccess": "Zahlung als durchgeführt markiert!",
    "payments.enterRejectionReason": "Geben Sie den Ablehnungsgrund an",
    "payments.rejectionReason": "Ablehnungsgrund",
    "payments.markAsPaid": "Als bezahlt markieren",
    
    // Employees
    "employees.title": "Mitarbeiter",
    "employees.addEmployee": "Mitarbeiter hinzufügen",
    "employees.type": "Typ",
    "employees.since": "Seit",
    "employees.viewDetails": "Details anzeigen",
    
    // Notifications
    "notifications.title": "Benachrichtigungen",
    "notifications.markAllRead": "Alle als gelesen markieren",
    "notifications.noNotifications": "Keine Benachrichtigungen",
    "notifications.newMessage": "Neue Nachricht",
    "notifications.newSale": "Neuer Verkauf",
    "notifications.pipelineUpdate": "Pipeline-Update",
    "notifications.contentRequest": "Inhaltsanfrage",
    
    // Level badges
    "level.bronze": "Bronze",
    "level.silver": "Silber",
    "level.gold": "Gold",
    "level.platinum": "Platin",
    "level.diamond": "Diamant",
    
    // Activity descriptions
    "activity.settingsUpdate": "Persönliche Informationen aktualisiert",
    "activity.passwordChange": "Kontopasswort geändert",
    "activity.photoUpdate": "Profilfoto aktualisiert",
    "activity.calendarUpdate": "Kalender aktualisiert",
    "activity.calendarComplete": "Kalender abgeschlossen",
    "activity.languageChange": "Sprache geändert zu",
    
    // Toast messages
    "toast.infoUpdated": "Informationen erfolgreich aktualisiert!",
    "toast.infoError": "Fehler beim Speichern der Informationen",
    "toast.passwordChanged": "Passwort erfolgreich geändert!",
    "toast.passwordError": "Fehler beim Ändern des Passworts",
    "toast.currentPasswordWrong": "Aktuelles Passwort ist falsch",
    "toast.passwordsNoMatch": "Die Passwörter stimmen nicht überein",
    "toast.fillAllFields": "Bitte füllen Sie alle Passwortfelder aus",
    "toast.enterCurrentPassword": "Geben Sie Ihr aktuelles Passwort ein",
    "toast.selectValidImage": "Bitte wählen Sie ein gültiges Bild",
    "toast.imageMaxSize": "Das Bild darf maximal 5MB haben",
    "toast.photoUpdated": "Foto erfolgreich aktualisiert!",
    "toast.photoError": "Fehler beim Hochladen des Fotos",
    "toast.languageChanged": "Sprache erfolgreich geändert!",
    "toast.languageError": "Fehler beim Ändern der Sprache",

    // Daily Entries (Chatter)
    "dailyEntries.title": "Tägliche Einträge",
    "dailyEntries.subtitle": "Erfassen Sie die täglichen Werte Ihrer Models pro Plattform",
    "dailyEntries.newEntry": "Neuer Eintrag",
    "dailyEntries.model": "Model",
    "dailyEntries.selectModel": "Wählen Sie ein Model",
    "dailyEntries.registerValues": "Werte erfassen",
    "dailyEntries.history": "Eintragshistorie",
    "dailyEntries.dateTime": "Datum/Uhrzeit",
    "dailyEntries.platform": "Plattform",
    "dailyEntries.value": "Wert ($)",
    "dailyEntries.noEntries": "Keine Einträge gefunden",
    "dailyEntries.enterAtLeastOne": "Geben Sie mindestens einen Wert ein",
    "dailyEntries.selectModelError": "Wählen Sie ein Model",
    "dailyEntries.valuesRegistered": "Werte erfasst für",
    "dailyEntries.saveError": "Fehler beim Speichern der Werte",

    // Exclusive Content Page
    "excPage.title": "EXKLUSIVER INHALT",
    "excPage.subtitleChatter": "Anfragen für Ihre {count} verbundene(n) Model(e)",
    "excPage.subtitleManage": "Anfragen für exklusiven Inhalt verwalten",
    "excPage.subtitleView": "Anfragen für exklusiven Inhalt anzeigen",
    "excPage.newRequest": "Neue Anfrage",
    "excPage.createRequest": "INHALTSANFRAGE ERSTELLEN",
    "excPage.model": "Model",
    "excPage.connected": "Verbunden",
    "excPage.selectModel": "Wählen Sie ein Model",
    "excPage.noConnectedModels": "Keine verbundenen Models",
    "excPage.noModelsAvailable": "Keine Models verfügbar",
    "excPage.noArtisticName": "Kein Künstlername",
    "excPage.requestDescription": "Anfragebeschreibung",
    "excPage.descriptionPlaceholder": "Beschreiben Sie den benötigten Inhalt...",
    "excPage.exampleImage": "Beispielbild (optional)",
    "excPage.clickToAttach": "Klicken Sie, um ein Bild anzuhängen",
    "excPage.imageFormats": "JPG, PNG oder GIF (max. 5MB)",
    "excPage.sending": "Senden...",
    "excPage.createOrder": "Anfrage erstellen",
    "excPage.connectedModels": "Verbundene Models",
    "excPage.noConnectedWait": "Keine verbundenen Models. Warten Sie, bis der Admin Models mit Ihnen verbindet.",
    "excPage.noName": "Kein Name",
    "excPage.searchPlaceholder": "Nach Model oder Beschreibung suchen...",
    "excPage.total": "Gesamt",
    "excPage.pending": "Ausstehend",
    "excPage.delivered": "Geliefert",
    "excPage.pendingRequests": "Ausstehende Anfragen",
    "excPage.noPending": "Keine ausstehenden Anfragen.",
    "excPage.reapproved": "Nachgebessert",
    "excPage.complaint": "Beschwerde",
    "excPage.addComplaint": "Beschwerde hinzufügen",
    "excPage.describeProblem": "Beschreiben Sie das Problem...",
    "excPage.send": "Senden",
    "excPage.cancel": "Abbrechen",
    "excPage.confirm": "Bestätigen",
    "excPage.delete": "Löschen",
    "excPage.requestDetails": "ANFRAGEDETAILS",
    "excPage.status": "Status",
    "excPage.deliveredStatus": "Geliefert",
    "excPage.pendingStatus": "Ausstehend",
    "excPage.reapprovedStatus": "Nachgebessert",
    "excPage.createdAt": "Erstellt am:",
    "excPage.deliveredAt": "Geliefert am:",
    "excPage.requestDesc": "Anfragebeschreibung:",
    "excPage.exampleImageLabel": "Beispielbild:",
    "excPage.complaintSent": "Gesendete Beschwerde:",
    "excPage.sentAt": "Gesendet am",
    "excPage.complaintNote": "Beim Senden einer Beschwerde wird der Inhalt als \"Nachgebessert\" markiert und das Model benachrichtigt.",
    "excPage.complaintPlaceholder": "Beschreiben Sie das Problem mit dem gelieferten Inhalt...",
    "excPage.sendComplaint": "Beschwerde senden",
    "excPage.cancelRequest": "Anfrage stornieren",
    "excPage.cancelTitle": "Anfrage stornieren",
    "excPage.cancelConfirm": "Sind Sie sicher, dass Sie diese exklusive Inhaltsanfrage stornieren möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
    "excPage.back": "Zurück",
    "excPage.cancelling": "Stornieren...",
    "excPage.confirmCancel": "Stornierung bestätigen",
    "excPage.selectChatterTitle": "Wählen Sie einen Chatter, um als dieser anzuzeigen",
    "excPage.impersonationNote": "Sie befinden sich im Impersonierungsmodus. Wählen Sie unten einen Chatter aus, um die Anfragen und verbundenen Models anzuzeigen.",
    "excPage.selectChatter": "Chatter auswählen",
    "excPage.noChatters": "Keine Chatters im System verfügbar.",
    "excPage.viewingAsChatter": "Anzeige als Chatter:",
    "excPage.switchChatter": "Chatter wechseln",
    "excPage.validImage": "Bitte wählen Sie ein gültiges Bild",
    "excPage.imageMaxSize": "Das Bild darf maximal 5MB groß sein",
    "excPage.fillRequired": "Bitte füllen Sie alle Pflichtfelder aus",
    "excPage.onlyConnected": "Sie können nur Anfragen für mit Ihnen verbundene Models erstellen",
    "excPage.noPermission": "Sie haben keine Berechtigung, Anfragen zu erstellen",
    "excPage.requestCreated": "Anfrage erfolgreich erstellt!",
    "excPage.requestCreatedImpersonated": "Anfrage erfolgreich erstellt (als impersonierter Chatter)!",
    "excPage.errorCreating": "Fehler beim Erstellen der Anfrage",
    "excPage.enterComplaint": "Geben Sie eine Beschwerde ein",
    "excPage.complaintSuccess": "Beschwerde gesendet! Das Model wird benachrichtigt.",
    "excPage.complaintError": "Fehler beim Senden der Beschwerde",
    "excPage.deliveryConfirmed": "Lieferung bestätigt!",
    "excPage.deliveryError": "Fehler beim Bestätigen der Lieferung",
    "excPage.requestCancelled": "Anfrage erfolgreich storniert!",
    "excPage.cancelError": "Fehler beim Stornieren der Anfrage",

    // Theme / Appearance
    "theme.title": "Erscheinungsbild",
    "theme.description": "Wählen Sie zwischen hellem oder dunklem Modus",
    "theme.darkMode": "Dunkelmodus",
    "theme.darkEnabled": "Der Dunkelmodus ist aktiviert",
    "theme.darkDisabled": "Dunkelmodus aktivieren",
  },
  pt: {
    // All Portuguese translations (defaults - will return key if not found)
  }
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>("pt");
  const [isLoading, setIsLoading] = useState(true);
  const { user, userRole } = useAuth();

  // Load language preference on mount
  useEffect(() => {
    async function loadLanguagePreference() {
      if (!user || userRole !== "employee") {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("language_preference, employee_type")
          .eq("user_id", user.id)
          .single();

        if (error) throw error;

        // Employee types that always use German
        const germanEmployeeTypes = ["chatter", "co_admin", "content_manager", "supervisor_chatter"];
        const isGermanEmployee = germanEmployeeTypes.includes(data?.employee_type || "");
        
        if (isGermanEmployee) {
          // These employee types always use German - set it if not already set
          if (data?.language_preference !== "de") {
            await supabase
              .from("user_roles")
              .update({ language_preference: "de" })
              .eq("user_id", user.id);
          }
          setLanguageState("de");
        } else if (data?.language_preference) {
          setLanguageState(data.language_preference as Language);
        }
      } catch (error) {
        console.error("Error loading language preference:", error);
      } finally {
        setIsLoading(false);
      }
    }

    loadLanguagePreference();
  }, [user, userRole]);

  const setLanguage = async (lang: Language) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("user_roles")
        .update({ language_preference: lang })
        .eq("user_id", user.id);

      if (error) throw error;

      setLanguageState(lang);
    } catch (error) {
      console.error("Error saving language preference:", error);
      throw error;
    }
  };

  const t = (key: string): string => {
    if (language === "pt") {
      // For Portuguese, return the key as fallback (original text)
      return translations.pt[key] || key;
    }
    
    // For German, return translation or key as fallback
    return translations.de[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
